import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, ExternalLink, Pencil, Save, Truck } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import ListPageHeader from '../common/ListPageHeader.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import plDeskService from '../../services/plDeskService.js';
import { getLocalDateString } from '../../utils/localDate.js';

const money = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const sourceHint = (row) => {
  if (row?.source === 'on_contract') return 'Suggested from vehicle On-Contract';
  if (row?.source === 'expense') return 'Saved as expense';
  return 'Enter amount (accountant)';
};

const AmountField = ({ label, row, value, onChange, disabled }) => (
  <label className="block min-w-0">
    <span className="block text-[11px] sm:text-xs font-semibold text-gray-600 mb-1">{label}</span>
    <input
      type="number"
      min="0"
      step="0.01"
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500"
    />
    <span className="block text-[10px] text-gray-400 mt-0.5">{sourceHint(row)}</span>
  </label>
);

const PlDeskPage = () => {
  const navigate = useNavigate();
  const [reportDate, setReportDate] = useState(getLocalDateString());
  const [desk, setDesk] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [editingIds, setEditingIds] = useState({});

  const applyDesk = (data) => {
    setDesk(data);
    const next = {};
    (data?.trips || []).forEach((trip) => {
      next[trip.ml_sheet_id] = {
        truck_hire: trip.truck_hire?.amount ? String(trip.truck_hire.amount) : '',
        freight_forwarding: trip.freight_forwarding?.amount ? String(trip.freight_forwarding.amount) : '',
        delivery_expenses: trip.delivery_expenses?.amount ? String(trip.delivery_expenses.amount) : '',
      };
    });
    setDrafts(next);
  };

  const loadDesk = useCallback(async (day) => {
    try {
      setLoading(true);
      const res = await plDeskService.getDesk(day);
      applyDesk(res?.data || { trips: [] });
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load daily P&L desk');
      setDesk({ trips: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDesk(reportDate);
  }, [reportDate, loadDesk]);

  const setDraft = (mlId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [mlId]: { ...(prev[mlId] || {}), [field]: value },
    }));
  };

  const saveTrip = async (trip) => {
    const draft = drafts[trip.ml_sheet_id] || {};
    try {
      setSavingId(trip.ml_sheet_id);
      const res = await plDeskService.saveCosts({
        report_date: reportDate,
        ml_sheet_id: trip.ml_sheet_id,
        freight_forwarding: draft.freight_forwarding,
        delivery_expenses: draft.delivery_expenses,
        truck_hire: trip.vehicle_id ? draft.truck_hire : 0,
        payment_mode: 'cash',
      });
      applyDesk(res?.data?.desk || desk);
      setEditingIds((prev) => ({ ...prev, [trip.ml_sheet_id]: false }));
      Alert.success('Expenses posted for this dispatch');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not save costs');
    } finally {
      setSavingId('');
    }
  };

  const trips = desk?.trips || [];

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6">
        <ListPageHeader
          title="Daily P&L Desk"
          icon={Calculator}
          subtitle="Post hire, forwarding and delivery costs from each dispatch. Auto lines stay on the report."
        >
          <Button
            variant="secondary"
            icon={ExternalLink}
            onClick={() => navigate(`/reports/route-pl?report_date=${reportDate}`)}
          >
            Open P&L Report
          </Button>
        </ListPageHeader>

        <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 mb-4 flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="block text-xs font-semibold text-gray-600 mb-1">Date</span>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm"
            />
          </label>
          <p className="text-xs text-gray-500 max-w-xl">
            Trip revenue and commission come from the ML. Truck hire prefills from the vehicle’s On-Contract.
            Save writes real expense records (same as Expense Management).
            Crossing only changes the vehicle — forwarding amount is entered here.
          </p>
        </div>

        {desk && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <p className="text-[10px] uppercase text-gray-500">Dispatches</p>
              <p className="text-lg font-bold text-gray-800">{trips.length}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <p className="text-[10px] uppercase text-gray-500">ML freight</p>
              <p className="text-lg font-bold text-gray-800">
                {money(trips.reduce((s, t) => s + Number(t.trip_revenue || 0), 0))}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <p className="text-[10px] uppercase text-gray-500">Admin / day</p>
              <p className="text-lg font-bold text-gray-800">{money(desk.admin_per_day)}</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/pl-statutory')}
              className="bg-white border border-gray-200 rounded-xl p-3 text-left hover:border-blue-300 hover:bg-blue-50/40"
            >
              <p className="text-[10px] uppercase text-gray-500">Overhead / day</p>
              <p className="text-lg font-bold text-gray-800">{money(desk.overhead_per_day)}</p>
              <p className="text-[10px] text-blue-600 mt-0.5">Edit payables</p>
            </button>
          </div>
        )}

        {loading ? (
          <Loader fullScreen={false} message="Loading dispatches…" />
        ) : trips.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-sm text-gray-500">
            No dispatches on this date. Create the ML first, then come back here to post costs.
          </div>
        ) : (
          <div className="space-y-3">
            {trips.map((trip) => {
              const draft = drafts[trip.ml_sheet_id] || {};
              const costsSaved = Boolean(
                trip.truck_hire?.source === 'expense'
                || trip.freight_forwarding?.source === 'expense'
                || trip.delivery_expenses?.source === 'expense',
              );
              const fieldsLocked = costsSaved && !editingIds[trip.ml_sheet_id];
              return (
                <div key={trip.ml_sheet_id} className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                        <Truck className="w-4 h-4 text-blue-600 shrink-0" />
                        {trip.ml_sheet_id}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">{trip.route_label}</p>
                      <p className="text-[11px] text-gray-500">
                        {trip.from_label} → {trip.to_label}
                        {trip.vehicle_number ? ` · ${trip.vehicle_number}` : ' · No vehicle'}
                        {trip.status ? ` · ${trip.status}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {fieldsLocked ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={Pencil}
                          onClick={() => setEditingIds((prev) => ({ ...prev, [trip.ml_sheet_id]: true }))}
                        >
                          Edit
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          icon={Save}
                          loading={savingId === trip.ml_sheet_id}
                          disabled={savingId === trip.ml_sheet_id}
                          onClick={() => saveTrip(trip)}
                        >
                          Save
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
                    <div className="rounded-lg bg-blue-50 px-2 py-2">
                      <p className="text-blue-700/80">Trip revenue</p>
                      <p className="font-bold text-blue-900">{money(trip.trip_revenue)}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 px-2 py-2">
                      <p className="text-gray-500">GCs</p>
                      <p className="font-bold text-gray-800">{trip.gc_count}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 px-2 py-2">
                      <p className="text-gray-500">Booking commission</p>
                      <p className="font-bold text-gray-800">{money(trip.booking_commission)}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 px-2 py-2">
                      <p className="text-gray-500">Delivery commission</p>
                      <p className="font-bold text-gray-800">{money(trip.delivery_commission)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <AmountField
                      label="Truck hire"
                      row={trip.truck_hire}
                      value={draft.truck_hire || ''}
                      onChange={(v) => setDraft(trip.ml_sheet_id, 'truck_hire', v)}
                      disabled={!trip.vehicle_id || fieldsLocked}
                    />
                    <AmountField
                      label="Freight forwarding"
                      row={trip.freight_forwarding}
                      value={draft.freight_forwarding || ''}
                      onChange={(v) => setDraft(trip.ml_sheet_id, 'freight_forwarding', v)}
                      disabled={fieldsLocked}
                    />
                    <AmountField
                      label="Delivery expenses"
                      row={trip.delivery_expenses}
                      value={draft.delivery_expenses || ''}
                      onChange={(v) => setDraft(trip.ml_sheet_id, 'delivery_expenses', v)}
                      disabled={fieldsLocked}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PlDeskPage;
